// src/hooks/useConvertedPrice.tsx
import { useEffect, useRef, useState } from "react";
import { convertPrice } from "../../utils/currency";

/**
 * Result shape returned by the hook
 */
export type ConvertedResult = {
  converted_price?: number | null;
  currency_code?: string | null;
  symbol?: string | null;
  source?: "localStorage" | "api" | "fallback" | null;
  loading: boolean;
  error?: string | null;
};

/**
 * In-memory cache so multiple components asking for the same price don't refetch.
 * Key format: `${country ?? 'unknown'}::${price}::${baseCurrency}`
 */
const conversionCache = new Map<string, ConvertedResult>();

/**
 * Build cache key
 */
function keyFor(price: number, baseCurrency = "USD", country?: string | null) {
  return `${country ?? "auto"}::${price}::${baseCurrency}`;
}

/**
 * Hook: useConvertedPrice
 *
 * - price: numeric price in baseCurrency (e.g. USD)
 * - options:
 *    - baseCurrency (default "USD")
 *    - forceRefresh (if true, ignore cached rate and re-call convertPrice)
 */
export function useConvertedPrice(
  price: number,
  options?: {
    baseCurrency?: string;
    forceRefresh?: boolean;
    geoTimeoutMs?: number;
  }
) {
  const baseCurrency = options?.baseCurrency ?? "USD";
  const forceRefresh = options?.forceRefresh ?? false;
  const geoTimeoutMs = options?.geoTimeoutMs;

  const mountedRef = useRef(true);

  const [state, setState] = useState<ConvertedResult>(() => {
    // If cached and not forcing refresh, return cached immediately
    const cachedKey = keyFor(price, baseCurrency, "auto");
    const cached = conversionCache.get(cachedKey);
    return (
      cached ?? {
        converted_price: null,
        currency_code: null,
        symbol: null,
        source: null,
        loading: true,
        error: null,
      }
    );
  });
  useEffect(() => {

    mountedRef.current = true;
    const cacheKey = keyFor(price, baseCurrency, "auto");
    const cached = conversionCache.get(cacheKey);

    // If we have cached and not forcing refresh → use it
    if (cached && !forceRefresh) {
      setState({ ...cached, loading: false });
      return () => {
        mountedRef.current = false;
      };
    }

    // mark loading state
    setState((prev) => ({ ...prev, loading: true, error: null }));

    let cancelled = false;

    (async () => {
      try {
        // call your robust convertPrice (it will re-acquire location if needed)
        const result = await convertPrice(price, baseCurrency, {
          geoTimeoutMs,
          forceRefresh,
        });

        if (cancelled || !mountedRef.current) return;
        if (!result) {
          const failure: ConvertedResult = {
            converted_price: null,
            currency_code: null,
            symbol: null,
            source: null,
            loading: false,
            error: "conversion_failed",
          };
          conversionCache.set(cacheKey, failure);
          setState(failure);
          return;
        }

        const success: ConvertedResult = {
          converted_price:
            // some APIs return converted_price, some return converted_amount — be flexible:
            (result as any).converted_price ??
            (result as any).converted_amount ??
            null,
          currency_code: (result as any).currency_code ?? null,
          symbol: (result as any).symbol ?? null,
          source: (result as any).source ?? "api",
          loading: false,
          error: null,
        };

        conversionCache.set(cacheKey, success);
        setState(success);
      } catch (err: any) {
        if (cancelled || !mountedRef.current) return;
        const failure: ConvertedResult = {
          converted_price: null,
          currency_code: null,
          symbol: null,
          source: null,
          loading: false,
          error: err?.message ?? "unknown_error",
        };
        conversionCache.set(cacheKey, failure);
        setState(failure);
      }
    })();

    return () => {
      cancelled = true;
      mountedRef.current = false;
    };
    // only re-run when price or baseCurrency or forceRefresh changes
  }, [price, baseCurrency, forceRefresh, geoTimeoutMs]);
  return state;
}
